import { useQuery, gql } from '@apollo/client';
import { t } from '@lingui/macro';
import React from 'react';

import RedirectWithStatus from 'common/CustomRoutes/RedirectWithStatus';
import { largestCities } from 'common/helpers/helpers';
import { RelatedPage } from 'pages/CompanyListing/CompanyListing.helpers';
import { AbstractCategory, TL4Category } from 'utils/graphql/categories';
import { TCity } from 'utils/graphql/geolocation';
import routes from 'utils/routeTranslator';
import { useSnackbar } from 'uikit/Snackbar';

export type L4CategoryTopSectionProps = {
  l4BySlug: TL4Category;
  cityBySlug?: TCity;
  neighbourhood?: {
    id: string;
    name: string;
    slug: string;
  };
  hash?: string;
};

export type L4Category = AbstractCategory & {
  imageUrl: string;
};

export type L2CategoryL4Group = {
  l4Categories: L4Category[];
};

export const getInterlinkingRelatedServices = (l4Groups: L2CategoryL4Group[]): L4Category[] => {
  return ([] as L4Category[]).concat(...l4Groups.map((group: L2CategoryL4Group) => group.l4Categories)).sort();
};

export const l4Redirects = {
  'remont-domu-mieszkania': 'remont-mieszkania',
  'ukladanie-paneli-podlog': 'ukladanie-paneli',
  'szpachlowanie-i-gladzenie-scian': 'szpachlowanie-scian',
  'remont-elewacji-i-ocieplenie-domu': 'elewacja-domu',
  'adaptacja-poddasza-strychu': 'ocieplenie-poddasza',
  'montaz-i-naprawa-drzwi': 'montaz-drzwi',
  'podwieszany-sufit-i-scianki-dzialowe': 'montaz-sufitu-podwieszanego',
  'instalacja-i-naprawa-skrzynki-z-bezpiecznikami': 'instalacje-elektryczne',
  'produkcja-i-montaz-ogrodzen-balustrad': 'balustrady',
  'produkcja-i-montaz-kuchni-na-wymiar': 'meble-kuchenne-na-wymiar',
  'produkcja-i-montaz-szaf-na-wymiar': 'szafy-na-wymiar',
  'montaz-i-skladanie-mebli': 'montaz-mebli',
  'instalacja-i-naprawa-centralnego-ogrzewania': 'instalacja-centralnego-ogrzewania',
  'montaz-i-naprawa-instalacji-elektrycznych': 'instalacje-elektryczne',
  'budowa-i-montaz-schodow': 'budowa-schodow-zewnetrznych',
  'malowanie-i-tapetowanie-wnetrz': 'malowanie-scian',
  'montaz-i-naprawa-kabin-prysznicowych': 'montaz-kabiny-prysznicowej',
  'montaz-i-naprawa-kranow-baterii': 'bialy-montaz',
  'montaz-i-naprawa-okien': 'montaz-okien',
  'montaz-i-naprawa-przelacznikow-gniazdek': 'pozostale-uslugi-elektryczne',
  'montaz-i-naprawa-wc': 'montaz-wc',
  'montaz-naprawa-oswietlenia': 'pozostale-uslugi-elektryczne',
  'montaz-zlewow-umywalek': 'bialy-montaz',
  'naprawa-i-ocieplenie-dachu': 'ocieplenie-dachu',
  'projektowanie-i-aranzacja-wnetrz': 'projektowanie-wnetrz',
  'remont-lazienki-toalety': 'remont-lazienki',
  'renowacja-i-odnawianie-mebli': 'renowacja-mebli',
  'budowa-domu': 'budowa-domu-jednorodzinnego',
  'dom-i-biuro': 'remont-mieszkania',
  'naprawa-lodowek': 'naprawa-lodowki',
  'zlota-raczka': {
    l2: true,
    slug: 'zlota-raczka',
  },
  // [GS-5926] Fix cannibalization of category semantics scope
  'montaz-drzwi-wewnetrznych': 'montaz-drzwi',
  'montaz-drzwi-zewnetrznych': 'montaz-drzwi',
  'montaz-instalacji-elektrycznej': 'instalacje-elektryczne',
  'nawadnianie-trawnika': 'nawadnianie-ogrodu',
  'ocieplenie-fundamentow': 'izolacja-fundamentow',
  'ocieplenie-poddasza-welna': 'ocieplenie-poddasza',
  'wyburzenie-budynku': 'rozbiorka-budynkow',
  'sprzatanie-domu': 'sprzatanie-mieszkan',
  'ukladanie-plytek-na-scianie': 'ukladanie-plytek',
  'ukladanie-plytek-podlogowych': 'ukladanie-plytek',
};

export type RelatedByL2Response = {
  l2BySlug: {
    id: string;
    l4Groups: L2CategoryL4Group[];
    categories: AbstractCategory[];
  };
};

export type RelatedByL2Vars = {
  slug?: string;
  city?: string;
};

export const l2Query = gql`
  query RelatedByL2($slug: String!, $city: String) {
    l2BySlug(slug: $slug, city: $city) {
      id
      l4Groups {
        l4Categories {
          id
          name
          slug
          imageUrl: mobileCoverUrl
        }
      }
      categories {
        id
        slug
        name
      }
    }
  }
`;

export const l4Redirect = (l4RedirectSlug: string | any, citySlug?: string) => {
  const redirectRoute =
    typeof l4RedirectSlug === 'string'
      ? routes.get('front::category', {
          'slug': l4RedirectSlug,
          city: citySlug,
        })
      : routes.get('front::categories.type', {
          'slug': l4RedirectSlug.slug,
          city: citySlug,
        });

  return <RedirectWithStatus status={301} to={redirectRoute} />;
};

export const neighbourhoodServices = [
  {
    name: 'Fotograf',
    slug: 'fotograf',
  },
  {
    name: 'Elektryk samochodowy',
    slug: 'elektryk-samochodowy',
  },
  {
    name: 'Biuro rachunkowe',
    slug: 'biuro-rachunkowe',
  },
  {
    name: 'Mechanik samochodowy',
    slug: 'mechanik-samochodowy',
  },
  {
    name: 'Dietetyk',
    slug: 'dietetyk',
  },
  {
    name: 'Pranie dywanów',
    slug: 'pranie-dywanow',
  },
  {
    name: 'Serwis telefonów',
    slug: 'serwis-telefonow',
  },
  {
    name: 'Radca prawny',
    slug: 'radca-prawny',
  },
  {
    name: 'Ślusarz',
    slug: 'slusarz',
  },
  {
    name: 'Wywóz śmieci',
    slug: 'wywoz-smieci',
  },
  {
    name: 'Pogotowie komputerowe',
    slug: 'pogotowie-komputerowe',
  },
  {
    name: 'Przeprowadzki',
    slug: 'przeprowadzki',
  },
  {
    name: 'Naprawa telewizorów',
    slug: 'naprawa-telewizorow',
  },
  {
    name: 'Serwis klimatyzacji',
    slug: 'serwis-klimatyzacji',
  },
  {
    name: 'Meble na wymiar',
    slug: 'meble-na-wymiar',
  },
  {
    name: 'Trener personalny',
    slug: 'trener-personalny',
  },
  {
    name: 'Wymiana oleju',
    slug: 'wymiana-oleju',
  },
  {
    name: 'Kurier',
    slug: 'kurier',
  },
  {
    name: 'Montaż anten',
    slug: 'montaz-anten',
  },
  {
    name: 'Ogrodnik',
    slug: 'ogrodnik',
  },
].sort((a, b) => (a.slug > b.slug ? 1 : -1));

/**
 * [GS-7415] L4+City dynamic length title
 * pattern description https://docs.google.com/spreadsheets/d/1hhsPQJyXxT5-kakdgVf0G8DURxNl5jvN3Q5EsZyrkmU/edit#gid=0
 */

const lengthBreakpoint = 50; // characters
const price = t`Cena`;
const feedback = t`Opinie`;
const priceAndFeedback = [price, feedback].join(', ');
const possibleSuffixes = [price, feedback, priceAndFeedback];
const separator = ' - ';

export const getCategoryPageTitle = (category: string, city: string, providersCount: number): string => {
  const defaultTitle = `${category} ${city}`;

  if (!providersCount) providersCount = 10;

  const getNextSuffix = (currentSuffix?: string): string | null => {
    const currentIndex = possibleSuffixes.findIndex((item) => item === currentSuffix);
    const nextIndex = currentIndex + 1;

    return nextIndex < possibleSuffixes.length ? possibleSuffixes[nextIndex] : null;
  };

  const concatTitle = (title: string, providers?: string, suffix?: string): string => {
    if (title.length < lengthBreakpoint) {
      const nextSuffix = getNextSuffix(suffix);
      if (providers) {
        if (!nextSuffix) return title;
        const nextTitle = [defaultTitle, providers, nextSuffix].join(separator);
        return concatTitle(nextTitle, providers, nextSuffix);
      } else {
        if (!nextSuffix) {
          const providers = t`${providersCount} wykonawców`;
          return concatTitle(defaultTitle, providers);
        }

        const nextTitle = [defaultTitle, nextSuffix].join(separator);
        return concatTitle(nextTitle, undefined, nextSuffix);
      }
    }
    return title;
  };

  return concatTitle(defaultTitle);
};

export const buildCompanyListingHref = (slug: string, city: string): string => {
  const companyListingCity = largestCities.some((item) => item.slug === city);
  return companyListingCity
    ? routes.get('front::companies.l4.city', {
        slug,
        city,
      })
    : routes.get('front::companies.l4', {
        slug,
      });
};

type RelatedCompaniesResponse = {
  companyListingPageData: {
    id: string;
    relatedPages: RelatedPage[];
  };
};

const relatedCompaniesQuery = gql`
  query RelatedCompanies($slug: String, $city: String) {
    companyListingPageData(pageSlug: $slug, citySlug: $city) {
      id
      relatedPages {
        id
        slug
        title
      }
    }
  }
`;

export const useRelatedCompanies = (slug?: string, city?: string) => {
  const { data } = useQuery<RelatedCompaniesResponse>(relatedCompaniesQuery, {
    skip: !slug,
    variables: {
      slug,
    },
  });

  if (data && data.companyListingPageData.relatedPages.length) {
    return data.companyListingPageData.relatedPages.map((item) => {
      return {
        title: item.title,
        url: buildCompanyListingHref(item.slug, city || ''),
        key: item.id,
      };
    });
  }
};

export const buildRegisterPageLinking = (categories: AbstractCategory[], city?: string) => {
  const isCityLargest = city && largestCities.some((item) => item.slug === city);
  return categories.map((item) => {
    const url = isCityLargest
      ? routes.get('front::provider.register.l3.city', {
          slug: item.slug,
          city,
        })
      : routes.get('front::provider.register.l3', { slug: item.slug });

    return {
      title: t`Zlecenia na ${item.name.toLowerCase()}`,
      url,
      key: item.id,
    };
  });
};
